/** One row of the categories list. */
export interface AttritionCategoryListItem {
  id: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  /** The live documents on the checklist. */
  documentCount: number;
  /** The live org units on the clearance list — zero is a valid answer. */
  clearanceCount: number;
  /** Live requests filed under it. */
  requestCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** One checklist line — the document asked for, and the account that reviews it. */
export interface AttritionCategoryDocument {
  id: string;
  name: string;
  description?: string | null;
  reviewerUserId: string;
  /** The account's display name, read LIVE. Omitted from the wire for an account that is gone. */
  reviewerName?: string | null;
  /** False when the reviewer's account is deactivated — the line then waits on nobody. */
  reviewerIsActive: boolean;
  sortOrder: number;
}

/** One clearance line — the org unit that must clear the employee, and the account that signs for it. */
export interface AttritionCategoryClearance {
  id: string;
  orgUnitId: string;
  /** The unit's name, read LIVE off the chart. */
  orgUnitName: string;
  /** False when the unit has since been deleted from the chart. */
  orgUnitIsActive: boolean;
  signatoryUserId: string;
  /** The account's display name, read LIVE. Omitted from the wire for an account that is gone. */
  signatoryName?: string | null;
  /** False when the signatory's account is deactivated — the line then waits on nobody. */
  signatoryIsActive: boolean;
  sortOrder: number;
}

export interface AttritionCategoryDetail {
  id: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  /** The live checklist, in order. */
  documents: AttritionCategoryDocument[];
  /** The live clearance list, in order — empty when the category asks for none. */
  clearances: AttritionCategoryClearance[];
  requestCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * One checklist line on the wire. `id` names a line already on the checklist so the requests
 * filed against it keep pointing at it; null adds one. Every row on screen is sent, blanks
 * included, so a rejection's index is the row the reader sees.
 */
export interface AttritionCategoryDocumentInput {
  id: string | null;
  name: string;
  description: string | null;
  reviewerUserId: string | null;
}

/**
 * One clearance line on the wire. No id: the UNIT is the line's key — a unit already listed is
 * updated, one taken off earlier is reactivated, a live one left out is flagged.
 */
export interface AttritionCategoryClearanceInput {
  orgUnitId: string | null;
  signatoryUserId: string | null;
}

/** Create and edit. The position and the counts are the server's. */
export interface AttritionCategoryRequest {
  name: string;
  description: string | null;
  documents: AttritionCategoryDocumentInput[];
  clearances: AttritionCategoryClearanceInput[];
}

/** The module's one list of what its writes touch — read by every History Logs surface. */
export const ATTRITION_CATEGORY_AUDIT_ENTITY_TYPES = ['AttritionCategory', 'AttritionCategoryDocument', 'AttritionCategoryClearance'];
